import { supabase } from "@/lib/supabase";
import { readHaulerScopedCarrierIdFromStorage } from "@/lib/roles/haulerAccess";
import { readWorkspaceOrgIdFromStorage } from "@/lib/workspaceOrgStorage";
import type { HaulerRoutesResponse } from "@/lib/logistics/haulerTypes";

type FetchResult<T> =
  | { ok: true; status: number; data: T }
  | { ok: false; status: number; error: string; body?: unknown };

async function haulerFetch<T>(path: string, init: RequestInit = {}): Promise<FetchResult<T>> {
  if (!supabase) return { ok: false, status: 503, error: "App is not configured for sign-in." };
  const {
    data: { session },
  } = await supabase.auth.getSession();
  const token = session?.access_token;
  if (!token) return { ok: false, status: 401, error: "Sign in required." };

  const headers: HeadersInit = {
    Authorization: `Bearer ${token}`,
    ...(init.headers || {}),
  };
  const ws = readWorkspaceOrgIdFromStorage();
  if (ws) (headers as Record<string, string>)["X-Workspace-Organization-Id"] = ws;
  const scopedCarrier = readHaulerScopedCarrierIdFromStorage();
  if (scopedCarrier) {
    (headers as Record<string, string>)["X-Logi-Scoped-Carrier-Id"] = scopedCarrier;
  }

  const r = await fetch(path, { ...init, headers });
  const text = await r.text();
  let json: unknown = null;
  try {
    json = JSON.parse(text);
  } catch {
    /* non-json */
  }

  if (!r.ok) {
    const err =
      typeof json === "object" && json !== null && "error" in json
        ? String((json as { error: unknown }).error)
        : text || r.statusText;
    return { ok: false, status: r.status, error: err, body: json };
  }
  return { ok: true, status: r.status, data: json as T };
}

export function fetchHaulerRoutes() {
  return haulerFetch<HaulerRoutesResponse>("/api/logi/hauler/routes", { method: "GET" });
}
